<script setup lang="ts">
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import AppCard from '../../primitives/AppCard/AppCard.vue'
import AppDrawnCheck from '../../primitives/AppDrawnCheck/AppDrawnCheck.vue'
import AppSpinner from '../../primitives/AppSpinner/AppSpinner.vue'
import { elementDisplayName } from '../../design/editor/design-element-name'
import { describeElement } from '@/lib/design/design-element'
import { useDesignStore } from '@/stores/design'
import { canUndoDesignEditRun, isDesignEditRunLive } from '@/types/design'
import { isLoaded } from '@/types/load-state'
import { editFailureKey, editStepStates } from './design-edit-card'

/** Raldo's edit of a design, as the chat shows it: the steps while it runs, then what changed with the way to undo it, or why nothing did. */
const props = defineProps<{
  designId: string
  runId: string
}>()

const { t } = useI18n()
const store = useDesignStore()
store.subscribeToDesign(props.designId)

const run = computed(
  () => store.editRunsFor(props.designId).find((candidate) => candidate.id === props.runId) ?? null,
)
const design = computed(() => store.designById(props.designId))
const undoing = ref(false)

const elementName = computed(() => {
  const eid = run.value?.eid
  const state = store.documentFor(props.designId)
  if (!eid || !run.value || !isLoaded(state)) return t('design.editor.raldo.card.elementFallback')
  const page = state.value.document.pages[run.value.page - 1]
  const element = page?.element.querySelector<HTMLElement>(`[data-eid="${eid}"]`)
  return element
    ? elementDisplayName(describeElement(element))
    : t('design.editor.raldo.card.elementFallback')
})

const steps = computed(() => {
  const current = run.value
  if (!current) return []
  const states = editStepStates(current.stage)
  const labels = [
    t('design.editor.raldo.steps.read', { page: current.page }),
    current.eid
      ? t('design.editor.raldo.steps.edit', { element: elementName.value })
      : t('design.editor.raldo.steps.editPage'),
    t('design.editor.raldo.steps.check'),
  ]
  return labels.map((label, index) => ({ label, state: states[index] ?? 'pending' }))
})

const canUndo = computed(() =>
  run.value ? canUndoDesignEditRun(run.value, design.value?.revision ?? null) : false,
)

async function undo(): Promise<void> {
  if (!run.value) return
  undoing.value = true
  await store.undoDesignEdit(run.value.id)
  undoing.value = false
}
</script>

<template>
  <AppCard v-if="run" dense data-design-edit-card class="max-w-(--message-max-width)">
    <ul v-if="isDesignEditRunLive(run)" class="flex list-none flex-col gap-4">
      <li
        v-for="step in steps"
        :key="step.label"
        :data-step-state="step.state"
        class="flex items-center gap-4 text-body-sm"
        :class="step.state === 'pending' ? 'text-text-tertiary' : 'text-text-primary'"
      >
        <span class="icon-sm grid size-12 shrink-0 place-items-center">
          <AppSpinner v-if="step.state === 'active'" size="md" :label="step.label" />
          <AppDrawnCheck v-else-if="step.state === 'done'" class="text-text-secondary" />
          <span v-else aria-hidden="true" class="size-4 rounded-full bg-border-strong" />
        </span>
        <span class="min-w-0 truncate">{{ step.label }}</span>
      </li>
    </ul>
    <div v-else-if="run.status === 'completed'" class="flex flex-col gap-4">
      <p class="text-body-sm text-text-primary">{{ run.summary }}</p>
      <p v-if="run.undone" class="text-caption text-text-tertiary">
        {{ t('design.editor.raldo.card.undone') }}
      </p>
      <div v-else class="flex">
        <AppButton
          variant="secondary"
          size="xs"
          :label="t('design.editor.raldo.card.undo')"
          :loading="undoing"
          :is-inactive="!canUndo"
          :inactive-reason="canUndo ? undefined : t('design.editor.raldo.card.undoSuperseded')"
          @click="undo"
        />
      </div>
    </div>
    <p v-else class="text-body-sm text-text-secondary">{{ t(editFailureKey(run)) }}</p>
  </AppCard>
</template>
